// FLAGS: identity, silent-failure
// The one place both generated HTTP clients are wired up: language header, distributed-tracing headers,
// duplicate-request cancellation, the 401/403 refresh-and-retry that decides when an operator is logged out,
// and the error hand-off to Application Insights. Nothing else in the app installs an axios interceptor.
import axios, {
  AxiosError,
  AxiosHeaders,
  AxiosResponse,
  InternalAxiosRequestConfig,
} from "axios";
import {
  OpenUpHttpClient,
  OpenUpHttpClientV2,
} from "@src/api/OpenUpHttpClient";
import { LANGUAGE_TYPES, apiPaths } from "@src/constants";
import { getApiBaseUrl } from "@src/utils/urlHelpers";
import { Users } from "@src/api/v2/Users";
import { getCurrentUser } from "@src/services/authentication.service";
import { httpErrorTrackingInterceptor } from "@src/utils/httpErrorTracking";
import { getTraceHeaders } from "@src/utils/appInsights";

interface OpenUpAxiosRequestConfig extends InternalAxiosRequestConfig {
  customRetryFlag?: boolean;
}

interface AuthCallbacks {
  logout: () => void;
  navigate: (path: string) => void;
  setIsLoggedIn: (value: boolean) => void;
  setUser: (user: null) => void;
}

// Module state
let isInitialized = false;
const pendingRequests = new Map();
let refreshPromise: Promise<void> | null = null;
const authorizationErrorStatusCodes = new Set([403, 401]);

// Callbacks to be set by React
let authCallbacks: AuthCallbacks = {
  logout: () => {},
  navigate: () => {},
  setIsLoggedIn: () => {},
  setUser: () => {},
};

/**
 * Set auth callbacks from React component.
 * Call this from AuthenticationProvider when callbacks change.
 */
export const setAuthCallbacks = (callbacks: AuthCallbacks) => {
  authCallbacks = callbacks;
};

const refreshAccessToken = () => new Users(OpenUpHttpClientV2).refreshToken({});

const setLanguageHeaderInterceptor = (config: InternalAxiosRequestConfig) => {
  config.headers["accept-language"] = LANGUAGE_TYPES.ENGLISH_GB;
  return config;
};

/**
 * Adds W3C Trace Context traceparent header and Request-Id for distributed tracing.
 */
const setTraceparentCorrelationHeaderInterceptor = (
  config: InternalAxiosRequestConfig,
) => {
  const traceHeaders = getTraceHeaders();
  if (!traceHeaders) return config;

  config.headers.traceparent = traceHeaders.traceparent;
  config.headers.set("Request-Id", traceHeaders.requestId);

  return config;
};

const requestInterceptor = (config: InternalAxiosRequestConfig) => {
  if (!config.headers) {
    config.headers = new AxiosHeaders();
  }
  let updatedConfig = setLanguageHeaderInterceptor(config);
  updatedConfig = setTraceparentCorrelationHeaderInterceptor(updatedConfig);
  return updatedConfig;
};

// FLAGS: silent-failure
// The identifier is URL plus method only, so two genuinely concurrent calls to the same endpoint are treated
// as a duplicate and the earlier one is cancelled — see uploadChatFile in acsFileUploadHandler.ts, where a
// multi-file selection loses every file but the last. Cancellations are also skipped by trackHttpError.
const cancelDuplicateRequests = (config: InternalAxiosRequestConfig) => {
  const requestIdentifier = `${config.url}_${config.method}`;

  if (pendingRequests.has(requestIdentifier)) {
    const cancelTokenSource = pendingRequests.get(requestIdentifier);
    cancelTokenSource.cancel("Cancelled due to new request");
  }

  const newCancelTokenSource = axios.CancelToken.source();
  config.cancelToken = newCancelTokenSource.token;
  pendingRequests.set(requestIdentifier, newCancelTokenSource);

  return config;
};

const handleRetryAfterRefresh = async (
  originalRequest: OpenUpAxiosRequestConfig,
) => {
  refreshPromise ??= refreshAccessToken().finally(() => {
    refreshPromise = null;
  });

  await refreshPromise;

  return axios(originalRequest);
};

const handleFailedAuth = () => {
  authCallbacks.navigate("/login");
  authCallbacks.logout();
};

const shouldRetryRequest = (
  error: AxiosError,
  originalRequest: OpenUpAxiosRequestConfig | undefined,
): boolean => {
  const isRefreshRequest = error.config?.url?.endsWith(apiPaths.refreshToken);
  const isLogoutRequest = error.config?.url === apiPaths.logout;
  const isTwoFactor = error.config?.url?.endsWith(apiPaths.verifyTwoFactorCode);
  const isRetry = originalRequest?.customRetryFlag ?? false;

  return (
    !isLogoutRequest &&
    !isRefreshRequest &&
    !isRetry &&
    !isTwoFactor &&
    !!originalRequest
  );
};

// FLAGS: identity
// Decides, for every 401 and 403 in the app, whether to silently refresh and retry or to force the operator
// out to /login. It refreshes only when a `user` entry is already in sessionStorage; the retry goes through the
// bare axios default instance, so it does not pass back through these interceptors a second time.
const refreshInterceptor = async (error: AxiosError) => {
  const originalRequest: OpenUpAxiosRequestConfig | undefined = error.config;
  const isLoginRequest = error.config?.url?.startsWith(apiPaths.externalLogin);
  const isLogoutRequest = error.config?.url === apiPaths.logout;
  const isAuthorizationError =
    !!error?.response?.status &&
    authorizationErrorStatusCodes.has(error?.response?.status);
  const isRetry = originalRequest?.customRetryFlag ?? false;

  if (!isAuthorizationError || isLoginRequest) {
    throw error;
  }

  if (!isLogoutRequest && isRetry) {
    handleFailedAuth();
    throw error;
  }

  if (shouldRetryRequest(error, originalRequest)) {
    originalRequest!.customRetryFlag = true;

    const currentUser = getCurrentUser();
    if (currentUser) {
      try {
        return await handleRetryAfterRefresh(originalRequest!);
      } catch {
        refreshPromise = null;
        // eslint-disable-next-line no-console
        console.debug("[Authorization] The refresh request failed");
        handleFailedAuth();
      }
    } else {
      authCallbacks.setIsLoggedIn(false);
      authCallbacks.setUser(null);
    }
  }

  throw error;
};

const removePendingRequest = (response: AxiosResponse) => {
  const requestIdentifier = `${response.config.url}_${response.config.method}`;
  pendingRequests.delete(requestIdentifier);
  return response;
};

const removePendingRequestErrorHandler = (error: AxiosError): never => {
  if (error.config) {
    const requestIdentifier = `${error.config.url}_${error.config.method}`;
    pendingRequests.delete(requestIdentifier);
  }
  throw error;
};

/**
 * Set up axios interceptors. Should be called once at app startup.
 * Safe to call multiple times - will only initialize once.
 */
export const setupAxiosInterceptors = () => {
  if (isInitialized) {
    return;
  }

  // Setup axios defaults
  OpenUpHttpClient.instance.defaults.baseURL = getApiBaseUrl();
  OpenUpHttpClient.instance.defaults.withCredentials = true;

  // Request interceptors - language header
  OpenUpHttpClient.instance.interceptors.request.use(requestInterceptor);
  OpenUpHttpClientV2.instance.interceptors.request.use(requestInterceptor);

  // Request interceptors - cancel duplicates
  OpenUpHttpClient.instance.interceptors.request.use(
    cancelDuplicateRequests,
    Promise.reject,
  );
  OpenUpHttpClientV2.instance.interceptors.request.use(
    cancelDuplicateRequests,
    Promise.reject,
  );

  // Response interceptors - token refresh
  OpenUpHttpClient.instance.interceptors.response.use(
    (response) => response,
    refreshInterceptor,
  );
  OpenUpHttpClientV2.instance.interceptors.response.use(
    (response) => response,
    refreshInterceptor,
  );

  // Response interceptors - HTTP error tracking to Application Insights
  OpenUpHttpClient.instance.interceptors.response.use(
    (response) => response,
    httpErrorTrackingInterceptor,
  );
  OpenUpHttpClientV2.instance.interceptors.response.use(
    (response) => response,
    httpErrorTrackingInterceptor,
  );

  // Response interceptors - pending request cleanup
  OpenUpHttpClient.instance.interceptors.response.use(
    removePendingRequest,
    removePendingRequestErrorHandler,
  );
  OpenUpHttpClientV2.instance.interceptors.response.use(
    removePendingRequest,
    removePendingRequestErrorHandler,
  );

  isInitialized = true;
};
